// src/components/preferences/SkillPreferenceEditor.tsx
/**
 * Editor for skill preferences (required, nice-to-have, learning skills)
 * ⚠️ FIXED: Uses correct field names matching backend schema
 */
import React from 'react';
import {
  IonItem,
  IonLabel,
  IonInput,
  IonList,
  IonChip,
  IonIcon,
  IonText,
  IonButton,
} from '@ionic/react';
import { closeCircle } from 'ionicons/icons';
import { SkillPreferences } from '../../types/preferences.ts';
import './PreferenceEditor.css';

interface SkillPreferenceEditorProps {
  data: SkillPreferences;
  onChange: (data: SkillPreferences) => void;
}

const SkillPreferenceEditor: React.FC<SkillPreferenceEditorProps> = ({ data, onChange }) => {
  const [newRequired, setNewRequired] = React.useState('');
  const [newNiceToHave, setNewNiceToHave] = React.useState('');
  const [newLearning, setNewLearning] = React.useState('');

  const handleAddItem = (
    field: 'required' | 'nice_to_have' | 'learning',
    value: string,
    setValue: (v: string) => void
  ) => {
    if (value.trim()) {
      const currentList = (data[field] || []) as string[];
      onChange({
        ...data,
        [field]: [...currentList, value.trim()],
      });
      setValue('');
    }
  };

  const handleRemoveItem = (
    field: 'required' | 'nice_to_have' | 'learning',
    index: number
  ) => {
    const currentList = (data[field] || []) as string[];
    onChange({
      ...data,
      [field]: currentList.filter((_, i) => i !== index),
    });
  };

  const renderChipInput = (
    field: 'required' | 'nice_to_have' | 'learning',
    label: string,
    placeholder: string,
    value: string,
    setValue: (v: string) => void,
    color: string = 'primary'
  ) => {
    const items = (data[field] || []) as string[];

    return (
      <IonItem>
        <IonLabel position="stacked">{label}</IonLabel>
        <div className="chip-input-container">
          {items.length > 0 && (
            <div className="chip-list">
              {items.map((item, index) => (
                <IonChip key={index} color={color}>
                  {item}
                  <IonIcon
                    icon={closeCircle}
                    onClick={() => handleRemoveItem(field, index)}
                  />
                </IonChip>
              ))}
            </div>
          )}
          <div className="chip-input">
            <IonInput
              value={value}
              placeholder={placeholder}
              onIonChange={(e) => setValue(e.detail.value || '')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddItem(field, value, setValue);
                }
              }}
            />
            <IonButton onClick={() => handleAddItem(field, value, setValue)} size="small">
              Add
            </IonButton>
          </div>
        </div>
      </IonItem>
    );
  };

  return (
    <IonList className="preference-editor-list">
      {renderChipInput(
        'required',
        'Required Skills',
        'e.g., Python, React, SQL',
        newRequired,
        setNewRequired,
        'primary'
      )}
      {renderChipInput(
        'nice_to_have',
        'Nice to Have Skills',
        'e.g., Docker, Kubernetes',
        newNiceToHave,
        setNewNiceToHave,
        'secondary'
      )}
      {renderChipInput(
        'learning',
        'Skills Interested in Learning',
        'e.g., Machine Learning, GraphQL',
        newLearning,
        setNewLearning,
        'tertiary'
      )}
    </IonList>
  );
};

export default SkillPreferenceEditor;
